import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart3, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { MockBadge } from '../components/MockBadge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/Select';
import { apiFetch } from '../lib/api';
import { splitLanguage } from '../lib/campaignGroups';
import { formatDateTime, formatMoney, formatNumber, formatPercent } from '../lib/format';
import { cn } from '../lib/utils';
import type { DashboardDay, DashboardItem, DashboardResponse } from '../types/api';

const DAY_MS = 24 * 60 * 60 * 1000;

const PERIOD_PRESETS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
] as const;

type Totals = DashboardResponse['totals'];

function ratio(part: number, whole: number): string {
  return whole > 0 ? formatPercent(part / whole) : '–';
}

/** "2026-10-31" → "31 Oct", read as a UTC day. */
function shortDay(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

function KpiStrip({ t, currency }: { t: Totals; currency: string }) {
  const tiles: { label: string; value: string; sub: string }[] = [
    {
      label: 'Revenue',
      value: formatMoney(t.revenue, currency),
      sub: t.sent > 0 ? `${formatMoney(t.revenue / t.sent, currency, 2)} per email` : '–',
    },
    {
      label: 'Orders',
      value: formatNumber(t.orders),
      sub: t.orders > 0 ? `${formatMoney(t.revenue / t.orders, currency)} per order` : '–',
    },
    { label: 'Sent', value: formatNumber(t.sent), sub: `${formatNumber(t.delivered)} delivered` },
    { label: 'Open rate', value: ratio(t.opened, t.sent), sub: `${formatNumber(t.opened)} opens` },
    {
      label: 'Click rate',
      value: ratio(t.clicked, t.sent),
      sub: `${formatNumber(t.clicked)} clicks`,
    },
    {
      label: 'Unsubscribed',
      value: ratio(t.unsubscribed, t.sent),
      sub: `${formatNumber(t.unsubscribed)} people · ${formatNumber(t.complained)} ${
        t.complained === 1 ? 'complaint' : 'complaints'
      }`,
    },
  ];
  return (
    <div
      role="list"
      aria-label="Key numbers"
      className="grid grid-cols-3 max-md:grid-cols-2 gap-px overflow-hidden rounded-[14px] border border-border bg-border"
    >
      {tiles.map((tile) => (
        <div
          role="listitem"
          key={tile.label}
          className="flex min-w-0 flex-col gap-0.5 bg-card px-4 py-3.5"
        >
          <span className="text-xs text-muted">{tile.label}</span>
          <span className="text-[26px] leading-tight font-semibold tracking-tight tabular-nums">
            {tile.value}
          </span>
          <span className="text-xs text-faint tabular-nums break-words">{tile.sub}</span>
        </div>
      ))}
    </div>
  );
}

const CHART_METRICS = [
  { key: 'revenue', label: 'Revenue' },
  { key: 'orders', label: 'Orders' },
  { key: 'sent', label: 'Sent' },
  { key: 'clicked', label: 'Clicks' },
] as const;
type ChartMetric = (typeof CHART_METRICS)[number]['key'];

/** Every day from `from` to `to`, with zeros where the rollup has no row. */
function fillDays(daily: DashboardDay[], from: string, to: string): DashboardDay[] {
  const byDay = new Map(daily.map((d) => [d.day, d]));
  const out: DashboardDay[] = [];
  for (let ms = Date.parse(from); ms <= Date.parse(to); ms += DAY_MS) {
    const day = new Date(ms).toISOString().slice(0, 10);
    out.push(byDay.get(day) ?? { day, sent: 0, opened: 0, clicked: 0, orders: 0, revenue: 0 });
  }
  return out;
}

const CHART_W = 600;
const CHART_H = 160;

function TrendChart({
  daily,
  from,
  to,
  currency,
}: {
  daily: DashboardDay[];
  from: string;
  to: string;
  currency: string;
}) {
  const [metric, setMetric] = useState<ChartMetric>('revenue');
  const [asTable, setAsTable] = useState(false);
  const days = useMemo(() => fillDays(daily, from, to), [daily, from, to]);
  const fmt = (v: number) => (metric === 'revenue' ? formatMoney(v, currency) : formatNumber(v));
  const max = Math.max(0, ...days.map((d) => d[metric]));
  const label = CHART_METRICS.find((m) => m.key === metric)?.label ?? '';
  const step = CHART_W / Math.max(1, days.length);
  const gap = Math.min(4, step * 0.25);

  return (
    <section aria-labelledby="trend-heading" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <h2 id="trend-heading" className="m-0 text-sm font-semibold mr-auto">
          Per day
        </h2>
        <Button
          variant="ghost"
          size="sm"
          className="sm:order-last"
          onClick={() => setAsTable((v) => !v)}
        >
          {asTable ? 'Show as chart' : 'Show as table'}
        </Button>
        <div
          role="group"
          aria-label="Metric"
          className="inline-flex rounded-lg border border-border bg-card p-0.5 max-sm:w-full max-sm:[&>button]:flex-1"
        >
          {CHART_METRICS.map((m) => (
            <button
              key={m.key}
              type="button"
              aria-pressed={metric === m.key}
              onClick={() => setMetric(m.key)}
              className={cn(
                'px-2.5 py-1 text-[13px] rounded-md text-muted hover:text-text',
                metric === m.key && 'bg-accent-soft text-accent font-medium hover:text-accent',
              )}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>
      {asTable ? (
        <div className="max-h-[260px] overflow-auto rounded-xl border border-border bg-card">
          <table className="w-full border-collapse text-[13px]" aria-label={`${label} per day`}>
            <thead>
              <tr className="text-left text-muted">
                <th className="font-normal px-3 py-1.5">Day</th>
                <th className="font-normal px-3 py-1.5 text-right">{label}</th>
              </tr>
            </thead>
            <tbody>
              {days.map((d) => (
                <tr key={d.day} className="border-t border-border-soft">
                  <td className="px-3 py-1.5">{shortDay(d.day)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmt(d[metric])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card px-4 pt-3 pb-2.5">
          <div className="text-[11px] text-faint tabular-nums mb-1">{fmt(max)}</div>
          <svg
            role="img"
            aria-label={`Bar chart: ${label.toLowerCase()} per day from ${shortDay(from)} to ${shortDay(to)}, highest ${fmt(max)}`}
            viewBox={`0 0 ${CHART_W} ${CHART_H}`}
            preserveAspectRatio="none"
            className="w-full h-[160px] border-b border-border"
          >
            <line x1="0" x2={CHART_W} y1="0.5" y2="0.5" className="stroke-border-soft" />
            <line
              x1="0"
              x2={CHART_W}
              y1={CHART_H / 2}
              y2={CHART_H / 2}
              className="stroke-border-soft"
            />
            {days.map((d, i) => {
              const h = max > 0 ? (Math.max(0, d[metric]) / max) * (CHART_H - 2) : 0;
              return (
                <rect
                  key={d.day}
                  x={i * step + gap / 2}
                  y={CHART_H - h}
                  width={Math.max(0, step - gap)}
                  height={h}
                  rx={1.5}
                  className="fill-accent"
                >
                  <title>{`${shortDay(d.day)}: ${fmt(d[metric])}`}</title>
                </rect>
              );
            })}
          </svg>
          <div className="flex justify-between text-[11px] text-muted mt-1.5">
            <span>{shortDay(from)}</span>
            <span>{shortDay(to)}</span>
          </div>
        </div>
      )}
    </section>
  );
}

function hasActivity(i: Totals): boolean {
  return i.sent > 0 || i.orders > 0 || i.revenue !== 0 || i.unsubscribed > 0;
}

const TH = 'px-3 py-2 bg-bg text-xs font-medium text-muted whitespace-nowrap text-right';
const TD = 'px-3 py-2.5 text-right tabular-nums';

/** One table row: a campaign or flow, or the language versions of one campaign added up. */
interface ItemRow {
  id: string;
  name: string;
  total: Totals;
  /** Language versions; empty for a single item. */
  members: { language: string; item: DashboardItem }[];
}

const METRICS: (keyof Totals)[] = [
  'sent',
  'delivered',
  'opened',
  'clicked',
  'unsubscribed',
  'bounced',
  'complained',
  'orders',
  'revenue',
];

function addUp(items: readonly DashboardItem[]): Totals {
  const t = Object.fromEntries(METRICS.map((k) => [k, 0])) as Totals;
  for (const i of items) for (const k of METRICS) t[k] += i[k];
  return t;
}

/** Groups "Halloween 2026 (DE)" with "Halloween 2026 (DA)", like the campaign list does. */
export function groupItems(items: readonly DashboardItem[]): ItemRow[] {
  const byName = new Map<string, { language: string; item: DashboardItem }[]>();
  for (const i of items) {
    const split = i.kind === 'campaign' ? splitLanguage(i.name) : null;
    if (!split) continue;
    const list = byName.get(split.name) ?? [];
    list.push({ language: split.language, item: i });
    byName.set(split.name, list);
  }
  const rows: ItemRow[] = [];
  const placed = new Set<string>();
  for (const i of items) {
    const split = i.kind === 'campaign' ? splitLanguage(i.name) : null;
    const members = split ? byName.get(split.name) : undefined;
    if (!split || !members || members.length < 2) {
      rows.push({ id: `${i.kind}:${i.key}`, name: i.name, total: i, members: [] });
      continue;
    }
    if (placed.has(split.name)) continue;
    placed.add(split.name);
    members.sort((a, b) => a.language.localeCompare(b.language));
    rows.push({
      id: `group:${split.name}`,
      name: split.name,
      total: addUp(members.map((m) => m.item)),
      members,
    });
  }
  return rows;
}

function MetricCells({ t, currency }: { t: Totals; currency: string }) {
  return (
    <>
      <td className={TD}>{formatNumber(t.sent)}</td>
      <td className={TD}>{ratio(t.opened, t.sent)}</td>
      <td className={TD}>{ratio(t.clicked, t.sent)}</td>
      <td className={TD}>{formatNumber(t.unsubscribed)}</td>
      <td className={TD}>{formatNumber(t.orders)}</td>
      <td className={cn(TD, 'font-semibold')}>{formatMoney(t.revenue, currency)}</td>
    </>
  );
}

function ItemTable({
  title,
  items,
  currency,
}: {
  title: string;
  items: DashboardItem[];
  currency: string;
}) {
  const [showQuiet, setShowQuiet] = useState(false);
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  if (items.length === 0) return null;
  const sorted = groupItems(items).sort(
    (a, b) => b.total.revenue - a.total.revenue || b.total.sent - a.total.sent,
  );
  const quiet = sorted.filter((r) => !hasActivity(r.total)).length;
  const rows = showQuiet ? sorted : sorted.filter((r) => hasActivity(r.total));
  const headingId = `dashboard-${title.toLowerCase()}`;
  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-2">
      <h2 id={headingId} className="m-0 text-sm font-semibold">
        {title} <span className="font-normal text-muted">{formatNumber(sorted.length)}</span>
      </h2>
      {rows.length > 0 ? (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm border-collapse" aria-label={title}>
            <thead>
              <tr>
                <th scope="col" className={cn(TH, 'text-left sticky left-0 z-10 min-w-[150px]')}>
                  Name
                </th>
                <th scope="col" className={TH}>
                  Sent
                </th>
                <th scope="col" className={TH}>
                  Open rate
                </th>
                <th scope="col" className={TH}>
                  Click rate
                </th>
                <th scope="col" className={TH}>
                  Unsub.
                </th>
                <th scope="col" className={TH}>
                  Orders
                </th>
                <th scope="col" className={TH}>
                  Revenue
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isOpen = open.has(r.id);
                return (
                  <Fragment key={r.id}>
                    <tr
                      className={cn(
                        'border-t border-border-soft',
                        !hasActivity(r.total) && 'text-faint',
                      )}
                    >
                      <th
                        scope="row"
                        className="px-3 py-2.5 text-left font-medium sticky left-0 bg-card"
                      >
                        {r.members.length > 0 ? (
                          <span className="flex items-center gap-2 min-w-0">
                            <button
                              type="button"
                              aria-expanded={isOpen}
                              aria-label={`Languages of ${r.name}`}
                              title={isOpen ? 'Hide the languages' : 'Show the languages'}
                              className="-m-1 p-1 shrink-0 rounded text-muted hover:text-text focus-visible:outline-2 focus-visible:outline-accent"
                              onClick={() => toggle(r.id)}
                            >
                              <ChevronRight
                                aria-hidden="true"
                                className={cn('size-4 transition-transform', isOpen && 'rotate-90')}
                              />
                            </button>
                            <span className="max-w-[220px] truncate" title={r.name}>
                              {r.name}
                            </span>
                            <span className="flex flex-wrap gap-1">
                              {r.members.map((m) => (
                                <span
                                  key={m.item.key}
                                  className="font-mono text-[11px] font-medium leading-none px-1.5 py-1 rounded border border-border text-muted"
                                >
                                  {m.language}
                                </span>
                              ))}
                            </span>
                          </span>
                        ) : (
                          <span className="block max-w-[260px] truncate" title={r.name}>
                            {r.name}
                          </span>
                        )}
                      </th>
                      <MetricCells t={r.total} currency={currency} />
                    </tr>
                    {isOpen
                      ? r.members.map((m) => (
                          <tr
                            key={`${m.item.kind}:${m.item.key}`}
                            className="border-t border-border-soft text-muted"
                          >
                            <th
                              scope="row"
                              className="pl-10 pr-3 py-2 text-left font-normal sticky left-0 bg-card"
                            >
                              <span className="font-mono text-[11px] font-medium">
                                {m.language}
                              </span>
                            </th>
                            <MetricCells t={m.item} currency={currency} />
                          </tr>
                        ))
                      : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      {quiet > 0 ? (
        <button
          type="button"
          onClick={() => setShowQuiet((v) => !v)}
          className="self-start text-[13px] text-muted hover:text-text"
        >
          {showQuiet
            ? 'Hide those with no activity'
            : `Show ${formatNumber(quiet)} with no activity in this period`}
        </button>
      ) : null}
    </section>
  );
}

/** While days are still being rolled up, the page asks again this often. */
const CATCH_UP_REFRESH_MS = 30_000;

/**
 * Where the numbers stand. The minute cron rolls about one missing day per
 * minute by itself, so there is nothing to press: the page refreshes while it
 * catches up and says roughly how long is left.
 */
export function RollupStatus({ data }: { data: DashboardResponse }) {
  const catchingUp = data.daysCatchingUp > 0;
  const missingOld = data.daysInRange - data.daysRolled - data.daysCatchingUp;
  const done = data.daysInRange === 0 ? 1 : data.daysRolled / data.daysInRange;
  return (
    <div role="status" className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
      <span className="inline-flex items-center gap-2 font-medium text-text">
        <span aria-hidden="true" className="relative inline-flex size-2">
          {catchingUp ? (
            <span className="absolute inset-0 rounded-full bg-warning-marker opacity-60 motion-safe:animate-ping" />
          ) : null}
          <span
            className={cn(
              'relative inline-block size-2 rounded-full',
              catchingUp ? 'bg-warning-marker' : 'bg-success-bar',
            )}
          />
        </span>
        {catchingUp ? 'Catching up' : 'Up to date'}
      </span>
      {catchingUp ? (
        <>
          <span
            role="progressbar"
            aria-label="Days ready"
            aria-valuemin={0}
            aria-valuemax={data.daysInRange}
            aria-valuenow={data.daysRolled}
            className="h-1.5 w-24 overflow-hidden rounded-full bg-border"
          >
            <span
              className="block h-full rounded-full bg-warning-marker transition-[width] duration-500"
              style={{ width: `${Math.round(done * 100)}%` }}
            />
          </span>
          <span>
            {formatNumber(data.daysRolled)} of {formatNumber(data.daysInRange)} days ready, the rest
            fills in by itself (
            {data.daysCatchingUp === 1
              ? 'about a minute left'
              : `about ${formatNumber(data.daysCatchingUp)} minutes left`}
            ).
          </span>
        </>
      ) : (
        <span>
          {data.lastRolledAt !== null
            ? `Updated ${formatDateTime(data.lastRolledAt)}, data up to ${data.rolledThrough}.`
            : 'Nothing was sent in this period.'}
          {data.daysNotFinal > 0
            ? ` The newest ${formatNumber(data.daysNotFinal)} days can still change a little as late opens and clicks come in.`
            : ''}
          {missingOld > 0
            ? ` ${formatNumber(missingOld)} of the oldest days are from before the summary started and stay empty.`
            : ''}
        </span>
      )}
    </div>
  );
}

/**
 * Results → Dashboard (K6 item 4). Revenue and engagement per campaign and per
 * flow over a chosen period, read only from the daily rollup (`stats_daily`),
 * never from live message or event tables.
 */
export function DashboardPage() {
  const [presetDays, setPresetDays] = useState<number>(30);

  const range = useMemo(() => {
    const to = new Date(Date.now() - DAY_MS).toISOString().slice(0, 10);
    const from = new Date(Date.parse(to) - (presetDays - 1) * DAY_MS).toISOString().slice(0, 10);
    return { from, to };
  }, [presetDays]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['stats', 'dashboard', range.from, range.to],
    queryFn: () =>
      apiFetch<DashboardResponse>(`/stats/dashboard?from=${range.from}&to=${range.to}`),
    refetchInterval: (query) =>
      (query.state.data?.daysCatchingUp ?? 0) > 0 ? CATCH_UP_REFRESH_MS : false,
  });

  const campaigns = data?.items.filter((i) => i.kind === 'campaign') ?? [];
  const flows = data?.items.filter((i) => i.kind === 'flow') ?? [];
  const t = data?.totals;
  const currency = data?.currency ?? 'EUR';

  return (
    <>
      <PageHeader
        title="Dashboard"
        badge={<MockBadge />}
        description={
          <>
            Revenue and engagement from campaigns and flows, {shortDay(range.from)} to{' '}
            {shortDay(range.to)}.
          </>
        }
        actions={
          <>
            <label htmlFor="dashboard-period" className="sr-only">
              Period
            </label>
            <Select value={String(presetDays)} onValueChange={(v) => setPresetDays(Number(v))}>
              <SelectTrigger id="dashboard-period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_PRESETS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        }
      />

      {isLoading ? <div className="text-muted mt-6">Loading…</div> : null}
      {isError ? <Alert>The dashboard could not be loaded.</Alert> : null}

      {data ? (
        <div className="mt-3 flex flex-col gap-1.5 text-[13px] text-muted">
          <RollupStatus data={data} />
          <details className="group">
            <summary className="inline-flex cursor-pointer list-none items-center gap-1 hover:text-text [&::-webkit-details-marker]:hidden">
              <ChevronRight
                size={14}
                aria-hidden="true"
                className="transition-transform group-open:rotate-90"
              />
              How these numbers are counted
            </summary>
            <p className="m-0 mt-1.5 pl-[18px] max-w-[75ch] text-pretty">
              Numbers come from a daily summary, not from live data (UTC days). Sent, delivered,
              opens, clicks, bounces and complaints are counted on the day the email was queued.
              Opens and clicks are unique emails, bots left out; late opens and clicks are added for
              8 days. Unsubscribes, orders and revenue are counted on the day they happened. Orders
              and revenue are only those the shop reported with a campaign or flow reference (paid
              orders, net of refunds); orders without it, such as direct or ad visits, are not
              included. A flow includes all its emails.
            </p>
          </details>
        </div>
      ) : null}

      {data && data.items.length === 0 ? (
        <div className="mt-6">
          <EmptyState
            icon={<BarChart3 />}
            title="No data in this period"
            description="Nothing was sent or ordered in the days that have been rolled up, or the rollup has not reached this period yet."
          />
        </div>
      ) : null}

      {data && t && data.items.length > 0 ? (
        <div className="mt-6 flex flex-col gap-8">
          <KpiStrip t={t} currency={currency} />
          <TrendChart daily={data.daily} from={data.from} to={data.to} currency={currency} />
          <ItemTable title="Campaigns" items={campaigns} currency={currency} />
          <ItemTable title="Flows" items={flows} currency={currency} />
        </div>
      ) : null}
    </>
  );
}
